import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Box, Button, Typography, TextInput } from "@strapi/design-system"
import { Plus, Trash } from "@strapi/icons"
import allLangsImport from "pages/TranslatesPage/allLangs"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// Only allow these languages: en, pt, lt, es, fr, it, de
const allowedLanguageCodes = ["en", "pt", "lt", "es", "fr", "it", "de"]
const allLangs = allLangsImport.filter((lang) => allowedLanguageCodes.includes(lang.code))

export interface ProductTranslation {
  code: string
  name: string
}

export interface SKUTranslation {
  code: string
  name: string
}

export interface ProductTranslationsData {
  productName: ProductTranslation[]
  skuNames: Record<string, SKUTranslation[]>
}

interface ProductTranslationsProps {
  productId: string
  skuIds: string[]
  defaultProductName: string
  defaultSkuNames: Record<string, string>
  defaultSkuNumbers?: Record<string, string>
  translations: ProductTranslationsData
  onChange: (translations: ProductTranslationsData) => void
  savedLanguages?: string[] // Languages that came from backend (can't be removed)
}

export const ProductTranslations: React.FC<ProductTranslationsProps> = ({
  productId: _productId,
  skuIds,
  defaultProductName,
  defaultSkuNames,
  defaultSkuNumbers,
  translations,
  onChange,
  savedLanguages = [],
}) => {
  const { t } = useTranslation()

  // Sync translations with new variants - ensure all variants have translations for all languages
  useEffect(() => {
    const existingSkuIds = Object.keys(translations.skuNames)
    const missingSkuIds = skuIds.filter((skuId) => !existingSkuIds.includes(skuId))
    
    if (missingSkuIds.length > 0 && translations.productName.length > 0) {
      const updatedSkuNames = { ...translations.skuNames }
      
      missingSkuIds.forEach((skuId) => {
        // Create empty translations for all existing languages for the new variant
        updatedSkuNames[skuId] = translations.productName.map((t) => ({
          code: t.code,
          name: "",
        }))
      })
      
      onChange({
        ...translations,
        skuNames: updatedSkuNames,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuIds.join(","), translations.productName.length])

  const availableLangs = allLangs.filter(
    (lang) => !translations.productName.some((t) => t.code === lang.code)
  )

  const handleAddLanguage = async (langCode: string) => {
    const lang = allLangs.find((l) => l.code === langCode)
    if (!lang) return

    const newProductTranslation: ProductTranslation = {
      code: langCode,
      name: "",
    }

    const newSkuTranslations: Record<string, SKUTranslation[]> = {}
    skuIds.forEach((skuId) => {
      newSkuTranslations[skuId] = [
        ...(translations.skuNames[skuId] || []),
        { code: langCode, name: "" },
      ]
    })

    const updated = {
      productName: [...translations.productName, newProductTranslation],
      skuNames: { ...translations.skuNames, ...newSkuTranslations },
    }

    onChange(updated)
  }

  const handleRemoveLanguage = (langCode: string) => {
    onChange({
      productName: translations.productName.filter((t) => t.code !== langCode),
      skuNames: Object.keys(translations.skuNames).reduce((acc, skuId) => {
        acc[skuId] = (translations.skuNames[skuId] || []).filter((t) => t.code !== langCode)
        return acc
      }, {} as Record<string, SKUTranslation[]>),
    })
  }

  const handleProductNameChange = (langCode: string, value: string) => {
    const updated = {
      ...translations,
      productName: translations.productName.map((t) =>
        t.code === langCode ? { ...t, name: value } : t
      ),
    }
    onChange(updated)
  }

  const handleSkuNameChange = (skuId: string, langCode: string, value: string) => {
    const updated = {
      ...translations,
      skuNames: {
        ...translations.skuNames,
        [skuId]: (translations.skuNames[skuId] || []).map((t) =>
          t.code === langCode ? { ...t, name: value } : t
        ),
      },
    }
    onChange(updated)
  }

  return (
    <Box
      background="neutral0"
      className="!p-6 rounded-lg flex flex-col gap-4"
      style={{ backgroundColor: "transparent" }}
    >
      <Box className="flex flex-col gap-4">
        {/* Default - current product state (no language) */}
        <Box
          className="flex flex-col gap-4 p-5 rounded-lg"
          style={{
            backgroundColor: "#374151",
            border: "1px solid #4b5563",
          }}
        >
          <Box
            className="flex items-center justify-between pb-3"
            style={{ borderBottom: "1px solid #4b5563" }}
          >
            <Typography
              variant="pi"
              fontWeight="semiBold"
              style={{ color: "#e5e7eb", fontSize: "14px" }}
            >
              Default
            </Typography>
          </Box>
          <Box className="flex flex-col gap-3">
            <TextInput
              label={t("productPage.productNameLabel")}
              name="product_name_default"
              value={defaultProductName}
              placeholder={defaultProductName}
              disabled={true}
            />
            {skuIds.length > 1 &&
              skuIds.map((skuId) => {
                const defaultSkuName = defaultSkuNames[skuId] || ""
                const skuNumber = defaultSkuNumbers?.[skuId] || ""
                const variantLabel = skuNumber ? `Variant - SKU: ${skuNumber}` : "Variant"
                return (
                  <TextInput
                    key={skuId}
                    label={variantLabel}
                    name={`sku_name_${skuId}_default`}
                    value={defaultSkuName}
                    placeholder={defaultSkuName}
                    disabled={true}
                  />
                )
              })}
          </Box>
        </Box>

        {/* Translations */}
        {translations.productName.length > 0 && (
          <>
            {translations.productName.map((translation) => {
              const lang = allLangs.find((l) => l.code === translation.code)
              const isSaved = savedLanguages.includes(translation.code)
              return (
                <Box
                  key={translation.code}
                  className="flex flex-col gap-4 p-5 rounded-lg"
                  style={{
                    backgroundColor: "#374151",
                    border: "1px solid #4b5563",
                  }}
                >
                  <Box
                    className="flex items-center justify-between pb-3"
                    style={{ borderBottom: "1px solid #4b5563" }}
                  >
                    <Typography
                      variant="pi"
                      fontWeight="semiBold"
                      style={{ color: "#e5e7eb", fontSize: "14px" }}
                    >
                      {lang?.lang || translation.code}
                    </Typography>
                    {!isSaved && (
                      <Button
                        variant="tertiary"
                        size="S"
                        startIcon={<Trash />}
                        onClick={() => handleRemoveLanguage(translation.code)}
                        style={{
                          backgroundColor: "transparent",
                          color: "#9ca3af",
                          border: "none",
                        }}
                      >
                        {t("productPage.remove")}
                      </Button>
                    )}
                  </Box>
                  <Box className="flex flex-col gap-3">
                    <TextInput
                      label={`${t("productPage.productNameLabel")} - ${defaultProductName}`}
                      name={`product_name_${translation.code}`}
                      onChange={(e) => handleProductNameChange(translation.code, e.target.value)}
                      value={translation.name}
                      placeholder={defaultProductName}
                    />
                    {skuIds.length > 1 &&
                      skuIds.map((skuId) => {
                        const skuTranslation = translations.skuNames[skuId]?.find(
                          (t) => t.code === translation.code
                        )
                        const defaultSkuName = defaultSkuNames[skuId] || ""
                        const skuNumber = defaultSkuNumbers?.[skuId] || ""
                        const variantLabel = skuNumber
                          ? `Variant - SKU: ${skuNumber} - ${defaultSkuName}`
                          : `Variant - ${defaultSkuName}`
                        return (
                          <TextInput
                            key={skuId}
                            label={variantLabel}
                            name={`sku_name_${skuId}_${translation.code}`}
                            onChange={(e) =>
                              handleSkuNameChange(skuId, translation.code, e.target.value)
                            }
                            value={skuTranslation?.name || ""}
                            placeholder={defaultSkuName}
                          />
                        )
                      })}
                  </Box>
                </Box>
              )
            })}
          </>
        )}
      </Box>
      {availableLangs.length > 0 && (
        <Box className="w-full mt-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button startIcon={<Plus />} size="M" variant="secondary" style={{ width: "100%" }}>
                {t("productPage.addTranslation")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              className="w-[var(--radix-dropdown-menu-trigger-width)] bg-gray-800 border-gray-600"
            >
              {availableLangs.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  className="flex cursor-pointer items-center gap-2 text-gray-200 hover:bg-gray-700 hover:text-white"
                  onClick={() => handleAddLanguage(lang.code)}
                >
                  {lang.lang} ({lang.code})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Box>
      )}
    </Box>
  )
}
